import { Box, Stack, Typography } from "@mui/material";
import { CONDENSED_FONT_FAMILY, DISPLAY_FONT_FAMILY, REPORT_RED } from "./constants";
import { useLeagueLogo } from "../data/LeagueEnvironmentProvider";
import RankPercentileLegend from "./OverviewPage/Header/RankPercentileLegend";
import { PreliminaryTag } from "./preliminary";

/**
 * Personnel-style running print header, repeated on every printed page via
 * PrintPageWrapper's table head.
 *
 * Slot semantics (2026-08-25 design call) — two identities, two fixed slots:
 *   LEFT  = org product stamp, constant: B/W pinwheel (the "Blazers internal"
 *           signature — never the subject's mark) + report label + as-of.
 *   RIGHT = the SUBJECT: the team this report is about, with THEIR logo and
 *           name in the report red (red slot = subject, not org), then the
 *           page context and the rank/percentile legend.
 * The pregame/matchup variant slots in via `reportLabel` (e.g.
 * "Pregame · PHX @ POR") when that report type lands — no layout change.
 */
export default function PrintHeader({
  season,
  teamId,
  teamName,
  contextLabel,
  asOfDate,
  rankPopulation,
  reportLabel = "Team Report",
  legendInline = false,
  preliminary = false,
}: {
  season?: number;
  /** Subject team's league id — renders their logo beside their name. */
  teamId?: number;
  teamName?: string;
  /** Right-side page context — "DENI AVDIJA · W · 04 / 15" or a section name. */
  contextLabel?: string;
  asOfDate?: string;
  rankPopulation?: number;
  /** Product segment; the future pregame variant overrides this. */
  reportLabel?: string;
  /** The legend inside the identity line, between the stamp and the subject, like the overview band's strip (the
   * ownership view's page 2, Trevor 2026-10-11); otherwise it rides on its own row above. */
  legendInline?: boolean;
  /** A game report still on CTG's same-night feed: the Preliminary tag after the as-of stamp */
  preliminary?: boolean;
}) {
  const getTeamLogoUrl = useLeagueLogo();
  const seasonLabel =
    season != null ? `${season}-${String((season + 1) % 100).padStart(2, "0")} ` : "";
  return (
    <Box sx={{ pb: 0.5, mb: 1, borderBottom: "2px solid #000", backgroundColor: "#fff" }}>
      {/* Legend rides above the identity line, top-center to match the
          overview band's placement. No negative top margin — inside the
          repeating print table-head that bled onto the previous page's
          bottom edge. */}
      {!legendInline && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 0, mb: 1 }}>
          <RankPercentileLegend rankPopulation={rankPopulation} />
        </Stack>
      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        {/* Org stamp — always the pinwheel, whatever team the report is for. */}
        <img
          src="/images/blazers-logo-black.png"
          alt=""
          height={20}
          width={19}
          style={{ objectFit: "contain" }}
        />
        <Typography
          component="span"
          sx={{
            fontFamily: DISPLAY_FONT_FAMILY,
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {seasonLabel}
          {reportLabel}
        </Typography>
        {asOfDate && (
          <Typography
            component="span"
            sx={{
              fontFamily: CONDENSED_FONT_FAMILY,
              fontSize: "0.6rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.disabled",
              whiteSpace: "nowrap",
            }}
          >
            As of {asOfDate}
          </Typography>
        )}
        {preliminary && <PreliminaryTag size="sm" />}
        <Box sx={{ flex: 1 }} />
        {legendInline && (
          <>
            <RankPercentileLegend rankPopulation={rankPopulation} />
            <Box sx={{ flex: 1 }} />
          </>
        )}
        {/* Subject: the team this report is FOR. */}
        {teamId != null && (
          <img src={getTeamLogoUrl(teamId)} alt="" height={18} />
        )}
        {teamName && (
          <Typography
            component="span"
            sx={{
              fontFamily: DISPLAY_FONT_FAMILY,
              fontWeight: 700,
              fontSize: "0.8rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: REPORT_RED,
              whiteSpace: "nowrap",
            }}
          >
            {teamName}
          </Typography>
        )}
        {contextLabel && (
          <Typography
            component="span"
            sx={{
              fontFamily: CONDENSED_FONT_FAMILY,
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "text.secondary",
              whiteSpace: "nowrap",
              // never push past the page edge at print widths
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              borderLeft: "1px solid #cfccc8",
              pl: 1,
            }}
          >
            {contextLabel}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
